// client/src/pages/Checkout.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, CheckCircle, ArrowLeft, XCircle,
  CreditCard, Wallet, X, Package,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { getErrorMessage } from '@/utils/errorUtils';
import storeService, {
  CheckoutCryptoChain,
  CheckoutCryptoSymbol,
  CheckoutInitResult,
  CheckoutOrderItem,
  CheckoutSummary,
  CheckoutPaymentOption,
  CheckoutPaymentType,
} from '@/api/services/storeService';
import tokenService from '@/api/services/tokenService';
import kxtService from '@/api/services/kxtService';
import DonationCryptoPaymentModal from '@/components/DonationCryptoPaymentModal';


const MaticIcon = ({ className = 'w-6 h-6' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 38.4 33.5" fill="currentColor">
    <path d="M29,10.2c-0.7-0.4-1.6-0.4-2.4,0L21,13.5l-3.8,2.1l-5.5,3.3c-0.7,0.4-1.6,0.4-2.4,0L5,16.3
      c-0.7-0.4-1.2-1.2-1.2-2.1v-5c0-0.8,0.4-1.6,1.2-2.1l4.3-2.5c0.7-0.4,1.6-0.4,2.4,0L16,7.2
      c0.7,0.4,1.2,1.2,1.2,2.1v3.3l3.8-2.2V7c0-0.8-0.4-1.6-1.2-2.1l-8-4.7c-0.7-0.4-1.6-0.4-2.4,0
      L1.2,5C0.4,5.4,0,6.2,0,7v9.4c0,0.8,0.4,1.6,1.2,2.1l8.1,4.7c0.7,0.4,1.6,0.4,2.4,0l5.5-3.2
      l3.8-2.2l5.5-3.2c0.7-0.4,1.6-0.4,2.4,0l4.3,2.5c0.7,0.4,1.2,1.2,1.2,2.1v5
      c0,0.8-0.4,1.6-1.2,2.1L29,28.8c-0.7,0.4-1.6,0.4-2.4,0l-4.3-2.5c-0.7-0.4-1.2-1.2-1.2-2.1V21
      l-3.8,2.2v3.3c0,0.8,0.4,1.6,1.2,2.1l8.1,4.7c0.7,0.4,1.6,0.4,2.4,0l8.1-4.7
      c0.7-0.4,1.2-1.2,1.2-2.1V17c0-0.8-0.4-1.6-1.2-2.1L29,10.2z"/>
  </svg>
);


type PaymentMethod = CheckoutPaymentType;
type CryptoPaymentDetails = {
  crypto: CheckoutCryptoSymbol;
  chain: CheckoutCryptoChain;
  baseFiat: string;
};
type PayerDetails = {
  email: string;
  phone: string;
  fullName: string;
};
type CryptoPaymentInitData = CheckoutInitResult;

const DEFAULT_CRYPTO_DETAILS: CryptoPaymentDetails = {
  crypto: 'USDT',
  chain: 'BSC_TESTNET',
  baseFiat: 'USD',
};

const DEFAULT_PAYER_DETAILS: PayerDetails = {
  email: '',
  phone: '',
  fullName: '',
};


const PAYMENT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  wallet: Wallet,
  card:   CreditCard,
  crypto: MaticIcon,
  credit_card: CreditCard,
  debit_card: CreditCard,
  mobile_money: CreditCard,
};


const Checkout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();


  const [orderItems, setOrderItems]           = useState<CheckoutOrderItem[] | null>(null);
  const [summary, setSummary]                 = useState<CheckoutSummary | null>(null);
  const [paymentOptions, setPaymentOptions]   = useState<CheckoutPaymentOption[]>([]);
  const [walletBalanceKxt, setWalletBalanceKxt] = useState<number | null>(null);
  const [walletBalanceUsd, setWalletBalanceUsd] = useState<number | null>(null);
  const [walletBalanceLoading, setWalletBalanceLoading] = useState(false);
  const [loading, setLoading]                 = useState(true);
  const [emptyCartError, setEmptyCartError]   = useState(false);
  const [processing, setProcessing]           = useState(false);
  const [message, setMessage]                 = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedMethod, setSelectedMethod]   = useState<PaymentMethod | null>(null);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showCryptoPaymentModal, setShowCryptoPaymentModal] = useState(false);
  const [showPaymentIframe, setShowPaymentIframe] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [payerDetails, setPayerDetails] = useState<PayerDetails>(DEFAULT_PAYER_DETAILS);
  const [cryptoDetails, setCryptoDetails] = useState<CryptoPaymentDetails>(DEFAULT_CRYPTO_DETAILS);
  const [cryptoPaymentData, setCryptoPaymentData] = useState<CryptoPaymentInitData | null>(null);
  const [isVerifyingCryptoPayment, setIsVerifyingCryptoPayment] = useState(false);
  const cryptoLiveMode = (import.meta.env.VITE_CRYPTO_LIVE as string | undefined) || 'live';
  const isCryptoTestMode = cryptoLiveMode.trim().toLowerCase() === 'test';


  const fetchCheckout = useCallback(async () => {
    try {
      setLoading(true);
      const [data, tokenSummary] = await Promise.all([
        storeService.getCheckoutSummary(),
        tokenService.getTokenSummary().catch(() => null),
      ]);
      setOrderItems(data.orderItems);
      setSummary(data.summary);
      setPaymentOptions(data.paymentOptions);

      const walletKxtBalance = tokenSummary?.walletBalance;
      if (typeof walletKxtBalance === 'number' && Number.isFinite(walletKxtBalance)) {
        setWalletBalanceKxt(walletKxtBalance);
        setWalletBalanceLoading(true);
        void kxtService
          .convertUsd(walletKxtBalance)
          .then((conversion) => {
            setWalletBalanceUsd(conversion.usd);
          })
          .catch((conversionError) => {
            console.error('Failed to convert KXT wallet balance to USD:', conversionError);
            setWalletBalanceUsd(null);
          })
          .finally(() => {
            setWalletBalanceLoading(false);
          });
      } else {
        setWalletBalanceKxt(null);
        setWalletBalanceUsd(null);
        setWalletBalanceLoading(false);
      }
    } catch (err: unknown) {
  
      if (err instanceof AxiosError && err.response?.status === 400) {
        setEmptyCartError(true);
      } else {
        showMessage('error', getErrorMessage(err) ?? t('operator.commerce.checkout.fetchError', {
          defaultValue: 'Failed to load checkout. Please try again.',
        }));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchCheckout(); }, [fetchCheckout]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    let msgText = text;
    setMessage({ type, text: msgText });
    setTimeout(() => setMessage(null), 6000);
  };


  const handleCheckoutClick = () => {
    setSelectedMethod(null);
    setPayerDetails(DEFAULT_PAYER_DETAILS);
    setCryptoDetails(DEFAULT_CRYPTO_DETAILS);
    setCryptoPaymentData(null);
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedMethod(null);
  };

  const closePaymentDetailsModal = () => {
    setShowPaymentDetailsModal(false);
    setShowPaymentModal(true);
  };

  const closeCryptoPaymentModal = () => {
    setShowCryptoPaymentModal(false);
    setCryptoPaymentData(null);
  };

  const closePaymentIframe = () => {
    setShowPaymentIframe(false);
    setPaymentLink(null);
  };

  const updatePayerDetails = (field: keyof PayerDetails, value: string) => {
    setPayerDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateCryptoDetails = (field: keyof CryptoPaymentDetails, value: string) => {
    setCryptoDetails((prev) => ({
      ...prev,
      [field]: value as CryptoPaymentDetails[typeof field],
    }));
  };

  const handlePaymentProceed = async () => {
    if (!selectedMethod) return;
    if (selectedMethod === 'crypto') {
      setShowPaymentModal(false);
      setShowPaymentDetailsModal(true);
      return;
    }

    setShowPaymentModal(false);
    setProcessing(true);
    const normalizedMethod = selectedMethod === 'wallet' ? 'wallet' : 'card';
    try {
      if (normalizedMethod === 'card') {
        const referralUrl =
          typeof window !== 'undefined' ? window.location.origin : undefined;
        const response = await storeService.initCheckoutPayment({
          paymentMethod: 'card',
          referralUrl,
        });
        if (!response?.data?.paymentLink) {
          throw new Error(response?.message || t('operator.commerce.checkout.errors.initPayment', {
            defaultValue: 'Failed to initialize payment',
          }));
        }
        if (response.data?.txRef) {
          localStorage.setItem('pendingCheckoutTxRef', response.data.txRef);
        }
        if (response.data?.orderId) {
          localStorage.setItem('pendingCheckoutOrderId', response.data.orderId);
        }
        setPaymentLink(response.data.paymentLink);
        setShowPaymentIframe(true);
        showMessage('success', response.message || t('operator.commerce.checkout.secureCheckout', {
          defaultValue: 'Secure Checkout',
        }));
        return;
      }

      const order = await storeService.placeOrder({ paymentMethod: normalizedMethod });
      showMessage('success', t('operator.commerce.checkout.success.orderPlaced', {
        defaultValue: 'Order placed successfully! Order ID: {{id}}',
        id: order.orderNumber || order.orderId,
      }));
      window.dispatchEvent(new Event('cartUpdated'));
      setTimeout(() => navigate('/operator/orders'), 2000);
    } catch (err: unknown) {
      showMessage('error', getErrorMessage(err) ?? t('operator.commerce.checkout.placeOrderError', {
        defaultValue: 'Failed to place order. Please try again.',
      }));
    } finally {
      setProcessing(false);
    }
  };

  const handleCryptoPaymentDetailsProceed = async () => {
    setShowPaymentDetailsModal(false);
    setProcessing(true);
    try {
      const response = await storeService.initCheckoutPayment({
        paymentMethod: 'crypto',
        crypto: cryptoDetails.crypto,
        chain: isCryptoTestMode ? 'BSC_TESTNET' : cryptoDetails.chain,
        baseFiat: cryptoDetails.baseFiat,
      });
      const paymentData = response?.data as CryptoPaymentInitData | null;
      if (!paymentData?.address) {
        throw new Error(response?.message || t('operator.commerce.checkout.errors.initPayment', {
          defaultValue: 'Failed to initialize payment',
        }));
      }
      setCryptoPaymentData(paymentData);
      setShowCryptoPaymentModal(true);
    } catch (err: unknown) {
      showMessage(
        'error',
        getErrorMessage(err) ??
          t('operator.commerce.checkout.placeOrderError', {
            defaultValue: 'Failed to place order. Please try again.',
          })
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleCryptoPaymentVerify = async () => {
    const reference = cryptoPaymentData?.reference || cryptoPaymentData?.transactionId;
    if (!reference) {
      showMessage('error', t('operator.commerce.checkout.crypto.missingReference', {
        defaultValue: 'Missing payment reference.',
      }));
      return;
    }

    try {
      setIsVerifyingCryptoPayment(true);
      const response = await storeService.verifyCheckoutPayment({ reference });
      if (!response?.success) {
        throw new Error(response?.message || t('operator.commerce.checkout.crypto.verifyFailed', {
          defaultValue: 'Payment verification failed.',
        }));
      }

      const verifiedOrderId = response?.data?.orderNumber || response?.data?.orderId;

      if (verifiedOrderId) {
        showMessage('success', response.message || t('operator.commerce.checkout.success.orderPlaced', {
          defaultValue: 'Order placed successfully! Order ID: {{id}}',
          id: verifiedOrderId,
        }));
      } else {
        const order = await storeService.placeOrder({
          paymentMethod: 'crypto',
          reference,
        });
        showMessage('success', t('operator.commerce.checkout.success.orderPlaced', {
          defaultValue: 'Order placed successfully! Order ID: {{id}}',
          id: order.orderNumber || order.orderId,
        }));
      }

      closeCryptoPaymentModal();
      window.dispatchEvent(new Event('cartUpdated'));
      setTimeout(() => navigate('/operator/orders'), 1500);
    } catch (err: unknown) {
      const apiError = getErrorMessage(err);
      const errorMessage = apiError ?? t('operator.commerce.checkout.crypto.verifyFailed', {
            defaultValue: 'Payment verification failed.',
          });
      showMessage('error', errorMessage);
    } finally {
      setIsVerifyingCryptoPayment(false);
    }
  };


  if (loading || orderItems === null) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }


  if (emptyCartError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center px-4">
        <div className="bg-white dark:bg-[#333436] rounded-2xl p-10 text-center max-w-sm w-full shadow-sm">
          <Package className="w-14 h-14 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
            {t('operator.commerce.checkout.emptyCartTitle', { defaultValue: 'Your cart is empty' })}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('operator.commerce.checkout.emptyCartBody', {
              defaultValue: 'Add items to your cart before proceeding to checkout.',
            })}
          </p>
          <button
            onClick={() => navigate('/operator/store')}
            className="px-6 py-2.5 bg-gray-900 dark:bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 dark:hover:bg-blue-500 transition-colors"
          >
            {t('operator.commerce.common.startShopping', { defaultValue: 'Browse Store' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('operator.commerce.checkout.meta.title', { defaultValue: 'Checkout | KonectX' })}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* Toast */}
          {message && (
            <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 border ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
            }`}>
              {message.type === 'success'
                ? <CheckCircle className="w-5 h-5 flex-shrink-0" />
                : <XCircle className="w-5 h-5 flex-shrink-0" />}
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/operator/cart')}
              className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-5"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('operator.commerce.checkout.backToCart', { defaultValue: 'Back to Cart' })}
            </button>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
              {t('operator.commerce.checkout.title', { defaultValue: 'Checkout' })}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
              {t('operator.commerce.checkout.subtitle', {
                defaultValue: 'Review your order and complete your purchase',
              })}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* ── Left: order items + activation info ── */}
            <div className="lg:col-span-2 space-y-6">

              {/* Order items */}
              <div className="bg-white dark:bg-[#333436] rounded-2xl p-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">
                  {t('operator.commerce.checkout.orderItems', { defaultValue: 'Order Items' })}
                </h2>
                <div className="space-y-4">
                  {orderItems.map((item) => (
                    <div
                      key={item.productId}
                      className="flex gap-4 items-center pb-4 border-b border-gray-100 dark:border-gray-700 last:border-0 last:pb-0"
                    >
                      <div className="w-20 h-20 bg-gray-50 dark:bg-[#1a1b1e] rounded-xl flex items-center justify-center flex-shrink-0">
                        <img
                          src={item.thumbnailUrl}
                          alt={item.name}
                          className="w-full h-full object-contain p-2"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'https://placehold.co/80x80/1a1a2e/60a5fa?text=KX';
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 dark:text-white truncate">{item.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          {t('operator.commerce.common.quantity', { defaultValue: 'Quantity' })}: {item.quantity}
                          {' · '}
                          ${item.unitPrice.toFixed(2)} {t('operator.commerce.common.each', { defaultValue: 'each' })}
                        </p>
                      </div>
                      {/* Use server lineTotal — no client multiplication */}
                      <p className="text-lg font-bold text-gray-800 dark:text-white flex-shrink-0">
                        ${item.lineTotal.toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Device activation steps */}
              <div className="bg-white dark:bg-[#333436] rounded-2xl p-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                  {t('operator.commerce.checkout.deviceActivation.title', { defaultValue: 'Device Activation' })}
                </h2>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 space-y-3">
                  {[
                    {
                      num: 1,
                      title: t('operator.commerce.checkout.deviceActivation.instantTitle', { defaultValue: 'Instant Activation' }),
                      body:  t('operator.commerce.checkout.deviceActivation.instantBody',  { defaultValue: 'Your devices will be activated immediately after order confirmation' }),
                    },
                    {
                      num: 2,
                      title: t('operator.commerce.checkout.deviceActivation.setupTitle',   { defaultValue: 'Setup Instructions' }),
                      body:  t('operator.commerce.checkout.deviceActivation.setupBody',    { defaultValue: "You'll receive setup guides via email and in your dashboard" }),
                    },
                    {
                      num: 3,
                      title: t('operator.commerce.checkout.deviceActivation.earningTitle', { defaultValue: 'Start Earning' }),
                      body:  t('operator.commerce.checkout.deviceActivation.earningBody',  { defaultValue: 'Begin earning rewards once your hotspots are online' }),
                    },
                  ].map(step => (
                    <div key={step.num} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {step.num}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-300">{step.title}</p>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">{step.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Right: order summary ── */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-[#333436] rounded-2xl p-6 sticky top-4">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">
                  {t('operator.commerce.common.orderSummary', { defaultValue: 'Order Summary' })}
                </h2>

                {/* Server-computed summary — no client-side calculation */}
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-600 dark:text-gray-400 text-sm">
                    <span>
                      {t('operator.commerce.common.subtotalItems', {
                        defaultValue: 'Subtotal ({{count}} items)',
                        count: summary!.itemCount,
                      })}
                    </span>
                    <span className="font-semibold">${summary!.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600 dark:text-gray-400 text-sm">
                    <span>{t('operator.commerce.common.platformFee', { defaultValue: 'Platform Fee' })}</span>
                    <span className="font-semibold">${summary!.platformFee.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                    <div className="flex justify-between text-xl font-bold text-gray-800 dark:text-white">
                      <span>{t('operator.commerce.common.total', { defaultValue: 'Total' })}</span>
                      <span>${summary!.total.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{summary!.currency}</p>
                  </div>
                </div>

                <button
                  onClick={handleCheckoutClick}
                  disabled={processing}
                  className="w-full py-4 bg-gray-800 dark:bg-blue-700 text-white rounded-xl hover:bg-gray-900 dark:hover:bg-blue-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('operator.commerce.checkout.processing', { defaultValue: 'Processing Order...' })}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      {t('operator.commerce.checkout.proceedToPayment', { defaultValue: 'Proceed to Payment' })}
                    </>
                  )}
                </button>

                <div className="mt-5 p-4 bg-gray-50 dark:bg-[#1a1b1e] rounded-xl">
                  <h3 className="font-semibold text-gray-800 dark:text-white mb-1 text-sm">
                    {t('operator.commerce.checkout.securePayment', { defaultValue: 'Secure Payment' })}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('operator.commerce.checkout.securePaymentBody', {
                      defaultValue: 'Your payment is processed securely. We accept wallet, cards, and crypto payments.',
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment modal */}
      <PaymentOptionsModal
        isOpen={showPaymentModal}
        onClose={closePaymentModal}
        selectedMethod={selectedMethod}
        onSelectMethod={setSelectedMethod}
        onProceed={handlePaymentProceed}
        paymentOptions={paymentOptions}
        walletBalanceKxt={walletBalanceKxt}
        walletBalanceUsd={walletBalanceUsd}
        walletBalanceLoading={walletBalanceLoading}
        totalAmount={summary!.total}
        currency={summary!.currency}
        isProcessing={processing}
      />

      <CryptoPaymentDetailsModal
        isOpen={showPaymentDetailsModal}
        onClose={closePaymentDetailsModal}
        onProceed={handleCryptoPaymentDetailsProceed}
        payerDetails={payerDetails}
        onPayerDetailsChange={updatePayerDetails}
        cryptoDetails={cryptoDetails}
        onCryptoDetailsChange={updateCryptoDetails}
        totalAmount={summary!.total}
        currency={summary!.currency}
        isCryptoTestMode={isCryptoTestMode}
        isProcessing={processing}
      />

      <CheckoutCryptoPaymentModal
        isOpen={showCryptoPaymentModal}
        onClose={closeCryptoPaymentModal}
        onPaid={handleCryptoPaymentVerify}
        isVerifying={isVerifyingCryptoPayment}
        data={cryptoPaymentData as unknown as Record<string, unknown> | null}
      />

      <KonnectXPaymentModal
        isOpen={showPaymentIframe}
        paymentLink={paymentLink}
        onClose={closePaymentIframe}
      />
    </>
  );
};

// ==================== PAYMENT MODAL ====================

interface PaymentOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMethod: PaymentMethod | null;
  onSelectMethod: (m: PaymentMethod) => void;
  onProceed: () => void;
  paymentOptions: CheckoutPaymentOption[];
  walletBalanceKxt: number | null;
  walletBalanceUsd: number | null;
  walletBalanceLoading: boolean;
  totalAmount: number;
  currency: string;
  isProcessing: boolean;
}

function PaymentOptionsModal({
  isOpen, onClose, selectedMethod, onSelectMethod,
  onProceed, paymentOptions, walletBalanceKxt, walletBalanceUsd, walletBalanceLoading, totalAmount, currency, isProcessing,
}: PaymentOptionsModalProps) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('operator.commerce.checkout.payment.choose', { defaultValue: 'Choose Payment Option' })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Total bar */}
        <div className="px-4 sm:px-6 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('operator.commerce.common.orderTotal', { defaultValue: 'Order Total' })}
          </p>
          <p className="text-2xl font-bold text-blue-600">
            ${totalAmount.toFixed(2)} <span className="text-sm font-normal text-gray-400">{currency}</span>
          </p>
        </div>

        {/* Payment options — rendered from API paymentOptions array */}
        <div className="p-4 sm:p-6 space-y-3">
          {paymentOptions.map((option) => {
            const Icon = PAYMENT_ICONS[option.type] ?? CreditCard;
            const isSelected = selectedMethod === option.type;
            return (
              <button
                key={option.type}
                onClick={() => option.available && onSelectMethod(option.type)}
                disabled={!option.available}
                className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                          {option.label}
                        </h3>
                        {option.type === 'wallet' && (
                          <div className="mt-0.5 space-y-0.5">
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                              {walletBalanceUsd !== null
                                ? t('operator.commerce.checkout.payment.walletBalance', {
                                    defaultValue: 'Balance in USD: ${{amount}}',
                                    amount: walletBalanceUsd.toFixed(2),
                                  })
                                : walletBalanceLoading
                                  ? t('operator.commerce.checkout.payment.walletBalanceLoading', {
                                      defaultValue: 'Balance in USD: converting...',
                                    })
                                  : t('operator.commerce.checkout.payment.walletBalanceUnavailable', {
                                      defaultValue: 'Balance in USD: unavailable',
                                    })}
                            </p>
                            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                              {walletBalanceKxt !== null
                                ? t('operator.commerce.checkout.payment.walletBalanceKxt', {
                                    defaultValue: 'Balance in KXT: {{amount}} KXT',
                                    amount: walletBalanceKxt.toFixed(2),
                                  })
                                : t('operator.commerce.checkout.payment.walletBalanceKxtUnavailable', {
                                    defaultValue: 'Balance in KXT: unavailable',
                                  })}
                            </p>
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {option.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-[#2a2b2e]">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {t('operator.commerce.common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={onProceed}
            disabled={!selectedMethod || isProcessing}
            className="px-8 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('operator.commerce.common.processing', { defaultValue: 'Processing...' })}
              </>
            ) : (
              t('operator.commerce.checkout.proceedToPayment', { defaultValue: 'Proceed to Payment' })
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

interface CryptoPaymentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  payerDetails: PayerDetails;
  onPayerDetailsChange: (field: keyof PayerDetails, value: string) => void;
  cryptoDetails: CryptoPaymentDetails;
  onCryptoDetailsChange: (field: keyof CryptoPaymentDetails, value: string) => void;
  totalAmount: number;
  currency: string;
  isCryptoTestMode: boolean;
  isProcessing: boolean;
}

function CryptoPaymentDetailsModal({
  isOpen,
  onClose,
  onProceed,
  payerDetails,
  onPayerDetailsChange,
  cryptoDetails,
  onCryptoDetailsChange,
  totalAmount,
  currency,
  isCryptoTestMode,
  isProcessing,
}: CryptoPaymentDetailsModalProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const isPayerFormValid = Boolean(
    payerDetails.email.trim() &&
      payerDetails.phone.trim() &&
      payerDetails.fullName.trim()
  );
  const isCryptoFormValid = Boolean(
    cryptoDetails.crypto &&
      cryptoDetails.chain &&
      cryptoDetails.baseFiat
  );

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] w-[92vw] max-w-[600px] max-h-[85vh] rounded-2xl shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('operator.commerce.checkout.crypto.completeTitle', { defaultValue: 'Complete Crypto Payment' })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label={t('operator.commerce.common.closeModal', { defaultValue: 'Close modal' })}
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {t('operator.commerce.common.orderTotal', { defaultValue: 'Order Total' })}
              </p>
              <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {t('operator.commerce.checkout.crypto.payWith', { defaultValue: 'Pay with Crypto' })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {t('operator.commerce.common.amount', { defaultValue: 'Amount' })}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">
                ${totalAmount.toFixed(2)} <span className="text-sm font-normal text-gray-400">{currency}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('enduser.plan.payment.emailLabel', { defaultValue: 'Email Address' })}
            </label>
            <input
              type="email"
              placeholder="your@email.com"
              value={payerDetails.email}
              onChange={(e) => onPayerDetailsChange('email', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('enduser.plan.payment.phoneLabel', { defaultValue: 'Phone Number' })}
            </label>
            <input
              type="tel"
              placeholder="+234 xxx xxx xxxx"
              value={payerDetails.phone}
              onChange={(e) => onPayerDetailsChange('phone', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('enduser.plan.payment.fullNameLabel', { defaultValue: 'Full Name' })}
            </label>
            <input
              type="text"
              placeholder="John Doe"
              value={payerDetails.fullName}
              onChange={(e) => onPayerDetailsChange('fullName', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 bg-gray-50 dark:bg-[#252628] space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Crypto
                </label>
                <select
                  value={cryptoDetails.crypto}
                  onChange={(e) => onCryptoDetailsChange('crypto', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USDT">USDT</option>
                  <option value="USDC">USDC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Chain
                </label>
                <select
                  value={cryptoDetails.chain}
                  onChange={(e) => onCryptoDetailsChange('chain', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >         
                  <option value="BSC_TESTNET">BSC Testnet</option>
                  <option value="SOLANA_DEVNET">SOLANA_DEVNET</option>
                  {/* <option value="POLYGON">Polygon</option> */}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('operator.commerce.common.amount', { defaultValue: 'Amount' })}
              </label>
              <input
                type="text"
                value={`${totalAmount.toFixed(2)} ${currency}`}
                readOnly
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white/80 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
              />
            </div>

            {isCryptoTestMode && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {t('operator.commerce.checkout.crypto.testMode', {
                  defaultValue: 'Test mode enabled. Chain will be sent as BSC_TESTNET.',
                })}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-[#2a2b2e]">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('operator.commerce.common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={onProceed}
            disabled={!isPayerFormValid || !isCryptoFormValid || isProcessing}
            className="px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('operator.commerce.common.processing', { defaultValue: 'Processing...' })}
              </>
            ) : (
              <span className="text-sm sm:text-base">
                {t('enduser.plan.actions.proceed', { defaultValue: 'Proceed to Payment' })}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckoutCryptoPaymentModal({
  isOpen,
  onClose,
  onPaid,
  isVerifying,
  data,
}: { isOpen: boolean; onClose: () => void; onPaid: () => void; isVerifying: boolean; data: Record<string, unknown> | null }) {
  return (
    <DonationCryptoPaymentModal
      isOpen={isOpen}
      onClose={onClose}
      onPaid={onPaid}
      isVerifying={isVerifying}
      data={
        data
          ? {
              ...data,
              message: "Send payment to the address below to complete your transaction.",
            }
          : null
      }
    />
  );
}

interface KonnectXPaymentModalProps {
  isOpen: boolean;
  paymentLink: string | null;
  onClose: () => void;
}

function KonnectXPaymentModal({
  isOpen,
  paymentLink,
  onClose,
}: KonnectXPaymentModalProps) {
  const { t } = useTranslation();
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '[::1]');

  useEffect(() => {
    if (!isOpen || !paymentLink || !isLocalhost) return;
    window.location.assign(paymentLink);
  }, [isOpen, paymentLink, isLocalhost]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-neutral-200/90 flex items-center justify-center z-50 p-4">
      <div className="bg-[#f2f2f2] w-[92vw] max-w-[420px] max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-neutral-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-[#f7f7f7]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-neutral-900 flex items-center justify-center">
              <span className="text-white text-sm font-semibold">KX</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">KonectX</p>
              <p className="text-xs text-neutral-500">
                {t('operator.commerce.checkout.secureCheckout', { defaultValue: 'Secure Checkout' })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-200 rounded-full transition-colors"
            aria-label={t('operator.commerce.common.closeModal', { defaultValue: 'Close modal' })}
          >
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="flex-1 bg-[#f2f2f2]">
          {paymentLink ? (
            isLocalhost ? (
              <div className="h-[70vh] flex items-center justify-center p-6 text-center">
                <div className="space-y-3">
                  <p className="text-sm text-neutral-700">
                    Localhost detected. Opening payment page in your browser.
                  </p>
                  <button
                    onClick={() => window.location.assign(paymentLink)}
                    className="px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm hover:bg-neutral-800 transition-colors"
                  >
                    Open Payment Page
                  </button>
                </div>
              </div>
            ) : (
              <iframe
                title={t('operator.commerce.checkout.flutterwaveFrameTitle', { defaultValue: 'Flutterwave Payment' })}
                src={paymentLink}
                className="w-full h-[70vh] border-0 bg-[#f2f2f2]"
                allow="payment"
              />
            )
          ) : (
            <div className="h-[70vh] flex items-center justify-center">
              <p className="text-sm text-neutral-600">
                {t('operator.commerce.checkout.loadingPaymentLink', { defaultValue: 'Loading payment link...' })}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Checkout;
